import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Icon } from '@iconify/react';
import { RootState, getApiBaseUrl } from '@circuitry-ai/doc-data';
import { useSorToken } from '@circuitry-ai/doc-ui';
import { validateWorkflowHtml } from '../../utils';

export type WorkflowHtmlMessage = {
  type: string;
  data?: unknown;
  timestamp?: string;
};

interface WorkflowHtmlPreviewProps {
  /** Inline HTML content, rendered after validation. Ignored when `src` is set. */
  html?: string;
  /** A presigned (or otherwise directly fetchable) URL to load in the iframe instead of inline `html`. */
  src?: string;
  /**
   * Renders `html` via the iframe's `srcDoc` attribute instead of a same-origin blob
   * URL. Only appropriate for the UXGenie template editor's own live-editing preview
   * (`UXGenieSplitPanel`), where the document legitimately regenerates on every
   * keystroke. Every other consumer should leave this unset — `html` then loads
   * through a blob URL `src`, a real navigation rather than `srcDoc`.
   */
  editorPreview?: boolean;
  title?: string;
  className?: string;
  onMessage?: (message: WorkflowHtmlMessage) => void;
}

const isWorkflowHtmlMessage = (payload: unknown): payload is WorkflowHtmlMessage => {
  return !!payload && typeof payload === 'object' && typeof (payload as WorkflowHtmlMessage).type === 'string';
};

export const WorkflowHtmlPreview: React.FC<WorkflowHtmlPreviewProps> = ({
  html,
  src,
  editorPreview = false,
  title = 'Generated HTML preview',
  className = '',
  onMessage,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  const [lastMessage, setLastMessage] = useState<WorkflowHtmlMessage | null>(null);
  const [isDomReady, setIsDomReady] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  const token = useSelector((state: RootState) => (state.auth as any)?.token ?? '');
  const authUserDetails = useSelector((state: RootState) => (state.auth as any)?.user?.details);
  const orgId = String(authUserDetails?.org_id ?? authUserDetails?.['https://circuitry.ai/org_id'] ?? '');
  // null when the tenant doesn't have SOR active
  const sorToken = useSorToken();

  // `src` (e.g. a presigned S3 URL) is loaded directly by the iframe — no need to
  // fetch/validate/inline it.
  const validation = useMemo(() => (src ? null : validateWorkflowHtml(html)), [html, src]);
  const validatedHtml = validation?.isValid ? validation.html : null;

  useEffect(() => {
    setLastMessage(null);
    setIsDomReady(false);
  }, [html, src]);

  // Build a same-origin blob URL for `validatedHtml` whenever there's no directly
  // loadable `src` and this isn't the editor's own live-editing preview — see
  // `editorPreview` above for why that one case still uses `srcDoc` instead.
  useEffect(() => {
    if (editorPreview || src || !validatedHtml) {
      setBlobUrl(null);
      return;
    }
    const url = URL.createObjectURL(new Blob([validatedHtml], { type: 'text/html' }));
    setBlobUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [editorPreview, src, validatedHtml]);

  // Tell the template's own document to render its built-in sample data once it
  // signals it's ready to receive messages (its `domReady` postMessage). Posting on
  // the iframe's `load` event instead would race the child's own message-listener
  // registration if it wires that up asynchronously (e.g. on DOMContentLoaded).
  useEffect(() => {
    if (!isDomReady) return;
    // Never target '*' — that would let whatever origin the frame happens to be
    // showing intercept this, including one it may have since navigated to.
    // Restrict delivery to the origin we actually intended: the presigned URL's own
    // origin for an externally supplied `src`, or this page's own origin otherwise
    // (both the editor's `srcDoc` and our own blob URL resolve to it).
    let targetOrigin: string;
    try {
      targetOrigin = src ? new URL(src, window.location.href).origin : window.location.origin;
    } catch {
      return;
    }
    // CL13606825 is a sample claim number for testing purposes. In a real application, you would replace this with actual data relevant to your workflow.
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'sampleData', value: true, token, sor_token: sorToken, org_id: orgId, base_url: getApiBaseUrl() },
      targetOrigin
    );
  }, [isDomReady, src, token, sorToken, orgId]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const payload = event.data;
      if (!isWorkflowHtmlMessage(payload)) return;

      const iframeWindow = iframeRef.current?.contentWindow;
      if (iframeWindow && event.source && event.source !== iframeWindow) return;

      // A `srcDoc` iframe (with allow-same-origin) and our own blob URL both resolve
      // to this page's own origin — but an externally supplied `src` (e.g. a
      // presigned S3 URL) is genuinely cross-origin, so the expected origin has to be
      // resolved from `src` in that case instead.
      let expectedOrigin: string | null = null;
      try {
        expectedOrigin = src ? new URL(src, window.location.href).origin : window.location.origin;
      } catch {
        expectedOrigin = null;
      }
      if (expectedOrigin && event.origin && event.origin !== expectedOrigin) return;

      if (payload.type === 'domReady') {
        setIsDomReady(true);
        return;
      }

      if (
        payload.type === 'formSubmit' ||
        payload.type === 'registrationComplete' ||
        payload.type === 'fileUpload' ||
        payload.type === 'stepChange'
      ) {
        setLastMessage(payload);
      }

      onMessageRef.current?.(payload);
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [src]);

  if (!src && !validatedHtml) {
    return (
      <div className={`flex flex-col items-center justify-center h-full min-h-[320px] p-8 text-center ${className}`}>
        <Icon icon="material-symbols:error-outline" className="text-red-500 mb-3" width={32} />
        <p className="text-sm font-semibold text-red-600">Unable to render HTML preview</p>
        <p className="text-xs text-default-500 mt-2 max-w-md">{validation?.error}</p>
      </div>
    );
  }

  return (
    <div className={`flex flex-col h-full min-h-0 ${className}`}>
      <iframe
        ref={iframeRef}
        title={title}
        {...(src
          ? { src }
          : editorPreview
            ? { srcDoc: validatedHtml as string }
            : { src: blobUrl ?? undefined })}
        // Matches the runtime template frame: templates open documents in a new tab, which needs
        // popups allowed and unsandboxed so the browser will render the opened PDF.
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads"
        allow="camera; microphone; fullscreen"
        className="w-full flex-1 min-h-[calc(100vh-220px)] border-0 bg-white"
      />

      {lastMessage && (
        <div className="flex-shrink-0 border-t border-[#E4E7EC] bg-[#F9FAFB] p-3 max-h-64 overflow-auto">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Icon icon="mdi:message-arrow-left-outline" width={16} className="text-primary" />
              <span className="text-xs font-semibold text-[#344054]">
                Received: {lastMessage.type}
              </span>
            </div>
            <button
              type="button"
              className="text-xs text-[#667085] hover:text-[#344054]"
              onClick={() => setLastMessage(null)}
            >
              Clear
            </button>
          </div>
          <pre className="text-xs text-[#101828] bg-white border border-[#E4E7EC] rounded-lg p-3 overflow-auto whitespace-pre-wrap break-words">
            {JSON.stringify(lastMessage.data ?? lastMessage, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};
